import React from "react";
import { ArrowRight, Sparkles, Award, Users2 } from "lucide-react";
import { Button } from "./ui/button";
import { heroImages, stats, brandLogos } from "../mock";

export default function Hero() {
  return (
    <section id="top" className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden">
      {/* decorative blobs */}
      <div className="blob w-[520px] h-[520px] bg-[#2c7a80]/15 -top-24 -left-24" />
      <div className="blob w-[420px] h-[420px] bg-[#e8a04d]/20 top-40 -right-24" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left: text */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-[#e6ded0] shadow-sm mb-6">
              <Sparkles className="w-3.5 h-3.5 text-[#e8a04d]" />
              <span className="text-xs font-medium tracking-wide text-[#0e3a3f]">
                Inscriptions ouvertes toute l'année
              </span>
            </div>

            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium text-[#0e3a3f] leading-[1.02] mb-6">
              Devenez{" "}
              <span className="relative inline-block">
                <span className="relative z-10">professionnel</span>
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" preserveAspectRatio="none">
                  <path d="M2,8 Q75,1 150,6 T298,4" stroke="#e8a04d" strokeWidth="4" fill="none" strokeLinecap="round"/>
                </svg>
              </span>
              <br />
              de la petite enfance.
            </h1>

            <p className="text-lg text-[#0e3a3f]/75 max-w-xl mb-8 leading-relaxed">
              Le CFMPE forme depuis 1996 aux métiers de la Petite Enfance à Nice.
              CAP AEPE en initial, alternance ou continu, avec un accompagnement personnalisé
              et un taux de réussite de 99%.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-12">
              <Button
                asChild
                size="lg"
                className="bg-[#2c7a80] hover:bg-[#245e63] text-white rounded-full px-7 h-12 text-base btn-primary group"
              >
                <a href="#cap-aepe">
                  Découvrir le CAP AEPE
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-[#0e3a3f]/15 hover:border-[#2c7a80] hover:bg-transparent text-[#0e3a3f] rounded-full px-7 h-12 text-base"
              >
                <a href="#financements">Voir les financements</a>
              </Button>
            </div>

            {/* stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-[#e6ded0]">
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-3xl md:text-4xl font-medium text-[#2c7a80]">{s.value}</div>
                  <div className="text-sm font-medium text-[#0e3a3f] mt-1">{s.label}</div>
                  <div className="text-xs text-[#0e3a3f]/50 mt-0.5">{s.detail}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: image collage */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] max-w-md mx-auto">
              {/* Main image */}
              <div className="absolute inset-0 rounded-3xl overflow-hidden shadow-2xl shadow-[#0e3a3f]/20">
                <img
                  src={heroImages.main}
                  alt="Bébé souriant"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "center 30%" }}
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#2c7a80]/20 to-transparent" />
              </div>

              {/* Floating card 1 - Qualiopi */}
              <div className="absolute -left-6 top-16 bg-white rounded-2xl p-3 shadow-xl shadow-[#0e3a3f]/10 border border-[#e6ded0] w-52 hover-lift">
                <div className="flex items-center gap-3">
                  <img src={brandLogos.qualiopi} alt="Qualiopi" className="h-10 w-auto object-contain" />
                  <div>
                    <div className="text-xs text-[#0e3a3f]/60">Certifié</div>
                    <div className="font-semibold text-sm text-[#0e3a3f]">Qualité</div>
                  </div>
                </div>
              </div>

              {/* Floating card 2 - Suivi */}
              <div className="absolute -right-4 bottom-12 bg-white rounded-2xl p-4 shadow-xl shadow-[#0e3a3f]/10 border border-[#e6ded0] w-56 hover-lift">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#2c7a80]/15 flex items-center justify-center">
                    <Users2 className="w-5 h-5 text-[#2c7a80]" />
                  </div>
                  <div>
                    <div className="text-xs text-[#0e3a3f]/60">Accompagnement</div>
                    <div className="font-semibold text-sm text-[#0e3a3f]">Suivi personnalisé</div>
                  </div>
                </div>
              </div>

              {/* Small decorative circles */}
              <div className="absolute -top-4 -right-6 w-20 h-20 rounded-full bg-[#e8a04d]/20 -z-10" />
              <div className="absolute -bottom-4 -left-4 w-24 h-24 rounded-full border-4 border-[#2c7a80]/20 -z-10" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
